import { useEffect, useRef, useState } from 'react'

import RenderIf from '@/components/ui/render-if'
import { Section } from '@/components/ui/section'
import { Text } from '@/components/ui/text'
import { TooltipInfo } from '@/components/ui/tooltip-v2'
import { CommonCheckCircle, CommonHourglass, CommonRefresh } from '@/icons'
import { cn } from '@/lib/utils'
import { IToken } from '@/types'

export type StepInfo = { id: number; label: string; icon: any; desc: string; tooltip?: string; token?: IToken }

export enum SwapStepNum {
  INIT = 0,
  CLAIM_OWNERSHIP = 1,
  APPROVE = 2,
  WAITING_TX = 3,
  SUBMITTING = 4,
}

type StepStatus = 'DONE' | 'IN_PROCESS' | 'INIT'

const StepStatusIcon: Record<StepStatus, React.ReactNode> = {
  INIT: <CommonHourglass className='size-6 text-white-48' />,
  IN_PROCESS: <CommonRefresh className='size-6 text-main-100  animate-spin' />,
  DONE: <CommonCheckCircle className='size-6  text-green-100' />,
}

export const SwapStepper = ({
  currentStep,
  steps,
}: {
  currentStep: number
  steps: StepInfo[]
  showWarningTransaction?: boolean
}) => {
  const [animationProgress, setAnimationProgress] = useState<Record<number, number>>({})
  const [barVisibility, setBarVisibility] = useState<Record<number, boolean>>({})

  const animationFrameRef = useRef<number | null>(null)
  const cycleTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const cleanup = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    if (cycleTimeoutRef.current !== null) {
      clearTimeout(cycleTimeoutRef.current)
      cycleTimeoutRef.current = null
    }
  }

  useEffect(() => {
    const initialProgress: Record<number, number> = {}
    const initialVisibility: Record<number, boolean> = {}

    steps.forEach((step, index) => {
      if (index < steps.length - 1) {
        initialProgress[step.id] = 0
        initialVisibility[step.id] = true
      }
    })

    setAnimationProgress(initialProgress)
    setBarVisibility(initialVisibility)

    const stepsToAnimate = steps
      .filter((step, index) => index < steps.length - 1 && step.id === currentStep)
      .map((step) => step.id)

    if (stepsToAnimate.length === 0) {
      cleanup()
      return
    }

    const animationDuration = 3000
    const fadeOutDuration = 1000
    const hiddenDuration = 100
    let startTime = Date.now()
    let isRunningAnimation = true

    const runAnimationCycle = () => {
      const animateProgress = () => {
        if (!isRunningAnimation) return

        const currentTime = Date.now()
        const elapsed = currentTime - startTime

        if (elapsed >= animationDuration) {
          isRunningAnimation = false

          stepsToAnimate.forEach((stepId) => {
            setBarVisibility((prev) => ({ ...prev, [stepId]: false }))
          })

          cycleTimeoutRef.current = setTimeout(() => {
            startTime = Date.now()
            isRunningAnimation = true

            stepsToAnimate.forEach((stepId) => {
              setBarVisibility((prev) => ({ ...prev, [stepId]: true }))
            })

            animationFrameRef.current = requestAnimationFrame(animateProgress)
          }, fadeOutDuration + hiddenDuration)

          return
        }

        const cycleProgress = (elapsed / animationDuration) * 100

        const newProgress = { ...animationProgress }
        stepsToAnimate.forEach((step) => {
          newProgress[step] = cycleProgress
        })
        setAnimationProgress(newProgress)

        animationFrameRef.current = requestAnimationFrame(animateProgress)
      }

      animationFrameRef.current = requestAnimationFrame(animateProgress)
    }

    cleanup()
    runAnimationCycle()

    return cleanup
  }, [currentStep, steps])

  return (
    <Section ring={false} className='relative mx-auto w-full order-2'>
      <div className='flex flex-col gap-5'>
        {steps.map((step, index) => {
          const isCompleted = step.id < currentStep
          const isSelected = step.id === currentStep
          const status = isCompleted ? 'DONE' : isSelected ? 'IN_PROCESS' : 'INIT'
          return (
            <div key={step.id} className='flex gap-2 items-center h-9'>
              <div className='gap-2 relative flex flex-col items-center'>
                <div
                  className={cn(
                    `flex size-8 z-1 center items-center justify-center rounded-sm`,
                    isSelected ? 'border-main-100 border' : 'border border-solid border-white-12',
                    isCompleted && 'border border-green-100'
                  )}
                >
                  {StepStatusIcon[status]}
                </div>
                <RenderIf condition={index < steps.length - 1}>
                  {/* bar */}
                  <div
                    className={cn(
                      `absolute md:top-[32px] top-[28px] md:h-6 h-[21px] w-[1px] rounded-sm overflow-hidden`,
                      isSelected ? 'bg-white-48' : isCompleted ? 'bg-green-100' : 'bg-white-48'
                    )}
                  >
                    {isSelected && (
                      <div
                        className={cn(
                          `absolute top-0 w-[1px] rounded-sm z-10`,
                          barVisibility[step.id] ? 'animate-bar-visible' : 'animate-bar-hidden',
                          'bg-main-100'
                        )}
                        style={{
                          height: `${((animationProgress[step.id] || 0) / 100) * 24}px`,
                        }}
                      ></div>
                    )}
                  </div>
                </RenderIf>
              </div>
              <div className='flex flex-1 gap-1 items-center'>
                <div className='flex flex-col gap-0.5'>
                  <div className='flex gap-1 items-center'>
                    <Text as='p' className={`t${isSelected ? 'text-white-100' : 'text-white-48'}`}>
                      {step.label}
                    </Text>
                    <p
                      className={`text-size-14 font-bold md:text-sm ${isSelected ? 'text-primary' : 'text-white-48'}`}
                    ></p>
                    {step.tooltip && (
                      <div className='h-fit flex items-center'>
                        <TooltipInfo content={step.tooltip} />
                      </div>
                    )}
                  </div>
                  <RenderIf condition={step.desc}>
                    <Text as='p' size={12} color='white-48'>
                      {step.desc}
                    </Text>
                  </RenderIf>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </Section>
  )
}
